import {closeDialog, openConfirmDialog, openDialog, showNotification} from 'platform/components';
import {isMatching, match} from 'ts-pattern';

import {head, isNil} from 'ramda';
import {isArray, isNonEmptyString, isString} from 'ramda-adjunct';

import {useLazyGetOriginalFileUrlQuery, useRetryMessageMutation} from '@dms/api/core';
import {i18n} from '@dms/i18n';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError, logError} from '@dms/shared';
import {DeliveryNoteDocumentPreview} from '@dms/warehouse/document-extraction';

import {type ApiError, pushGtmEvent} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const EXTRACTED_DELIVERY_NOTES = 'extracted-delivery-notes';
const PREDEFINED_COLUMNS = ['fileId', 'zipId'] as const;
const STORAGE_NAMESPACE = 'prompt-bridge';
const MESSAGE_NOT_RETRYABLE = 'prompt-bridge-081';

const isFilledString = (value: unknown): value is string =>
  isString(value) && isNonEmptyString(value);

export function ExtractedDeliveryNotes() {
  const [getOriginalFileUrl] = useLazyGetOriginalFileUrlQuery();
  const [retryMessage] = useRetryMessageMutation();

  const handleClose = () => {
    pushGtmEvent({
      event: 'button_click',
      event_id: 'ai_tool_dn_import_dialog_confirm_close',
    });
    closeDialog(DIALOG_ID);
  };

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    rowId,
    actionKey,
    getColumnData,
    refreshData,
  }) => {
    const id = isArray(rowId) ? head(rowId) : rowId;

    return match(actionKey)
      .with('redirectDetail', 'details', () => {
        if (isNil(id)) {
          return logError('id is not defined');
        }

        openDialog(
          <DeliveryNoteDocumentPreview
            messageId={id}
            onClose={() => {
              pushGtmEvent({
                event: 'button_click',
                event_id: 'ai_tool_dn_import_dialog_close',
              });
              openConfirmDialog({
                text: i18n.t('entity.documentExtraction.notifications.closeDialogConfirmation'),
                onConfirm: handleClose,
              });
            }}
          />,
          {
            id: DIALOG_ID,
            title: i18n.t('entity.documentExtraction.labels.documentPreview'),
            size: 'xLarge',
            withAdditionalFooter: true,
          }
        );
      })
      .with('preview', () => {
        if (isArray(rowId)) {
          return logError(`Bulk invocation of single-row action ${actionKey} is not implemented`);
        }

        const fileId = getColumnData('fileId')?.value;
        const previewFileId = isFilledString(fileId) ? fileId : getColumnData('zipId')?.value;

        if (!isFilledString(previewFileId)) {
          return logError('Missing fileId or zipId for document preview');
        }

        // Open synchronously so popup blockers allow the preview tab.
        const tab = window.open('', '_blank');

        if (isNil(tab)) {
          return logError('Unable to open document preview tab');
        }

        tab.opener = null;

        const fileRequest = {fileId: previewFileId, namespace: STORAGE_NAMESPACE};

        return getOriginalFileUrl(fileRequest)
          .unwrap()
          .then((url) => {
            if (isNil(url)) {
              tab.close();
              return logError('Empty document preview URL');
            }

            tab.location.href = url;
          })
          .catch((error: ApiError) => {
            tab.close();
            handleApiError(error);
          });
      })
      .with('retry', () => {
        if (isArray(rowId)) {
          return logError(`Bulk invocation of single-row action ${actionKey} is not implemented`);
        }

        // Returning the promise locks the action until it settles, so a second click cannot queue
        // a second extraction. The catch is load-bearing: the legacy engine does not unlock in a
        // finally, so a throw would leave the row disabled until it remounts.
        return retryMessage({id: rowId})
          .unwrap()
          .then(() => refreshData())
          .catch((error: ApiError) => {
            if (isMatching({data: {code: MESSAGE_NOT_RETRYABLE}}, error)) {
              showNotification.error(
                i18n.t('entity.documentExtraction.notifications.retryNotAllowed')
              );
              return;
            }

            handleApiError(error);
          });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <DmsDataGrid
      gridCode={EXTRACTED_DELIVERY_NOTES}
      predefinedColumns={PREDEFINED_COLUMNS}
      actionCallback={actionCallback}
    />
  );
}

const DIALOG_ID = 'document-extraction';
